import { useState } from 'react';
import { useDateNow, useReduxState } from 'hooks';
import { differenceInDays, endOfDay, startOfDay, subDays } from 'date-fns';
import { getContentTypeColors } from 'lib/analytics';
import { useApiData } from 'lib/api';
import { Box } from 'components/common/box';
import { DateRangeSelector } from 'components/common/charts';
import { MyTeamActivityChart, MyTeamActivityList } from '.';

import type { ActivityType } from 'shared-types';
import type { GetTeamActivitiesRes } from 'shared-types/responses';

export type ActivityTypeWithAll = ActivityType | 'all';

const MyTeamActivity = () => {
  const { messages, user } = useReduxState();
  const { dateNow } = useDateNow();

  const [fromDate, setFromDate] = useState(
    startOfDay(subDays(dateNow, 30)).toISOString()
  );
  const [toDate, setToDate] = useState(endOfDay(new Date()).toISOString());
  const [range, setRange] = useState<'day' | 'week' | 'month'>('day');
  const [selectedActivityType, setSelectedActivityType] =
    useState<ActivityTypeWithAll>('all');

  const { data, loading } = useApiData<GetTeamActivitiesRes>({
    url: `/admin/supervisors/${user?.id}/team-activities?fromDate=${fromDate}&toDate=${toDate}&activityType=${selectedActivityType}`,
    onlyIf: Boolean(user)
  });

  const handleDateRangeChanged = (values: {
    fromDate: string;
    toDate: string;
    range: 'day' | 'week' | 'month';
  }) => {
    if (!values.range) {
      const diffInDays = differenceInDays(
        Date.parse(values.toDate),
        Date.parse(values.fromDate)
      );

      values.range =
        diffInDays > 182 ? 'month' : diffInDays > 42 ? 'week' : 'day';
    }

    setFromDate(values.fromDate);
    setToDate(values.toDate);
    setRange(values.range);
  };

  const colorsByActivityType = getContentTypeColors();

  const namesByActivityType = {
    achievement: {
      message: messages.achievementNumberOfAchievements,
      placeholder: 'numberOfAchievements'
    },
    'calendar-event': {
      message: messages.calendarEventNumberOfCompletions,
      placeholder: 'numberOfEvents'
    },
    competency: {
      message: messages.competenciesNumberOfCompetencies,
      placeholder: 'numberOfCompetencies'
    },
    course: {
      message: messages.courseNumberOfCompletions,
      placeholder: 'numberOfCompletions'
    },
    'individual-achievement': {
      message: messages.individualAchievementsNumberOfAchievements,
      placeholder: 'numberOfAchievements'
    },
    path: {
      message: messages.pathsNumberOfCompletions,
      placeholder: 'numberOfCompletions'
    }
  };

  return (
    <Box
      headerContent={
        <div className="header">
          <DateRangeSelector
            size="header"
            updateData={handleDateRangeChanged}
          />
        </div>
      }
      headerContentWrap={true}
      padding={0}
      title={messages.commonActivity}
    >
      <div className="activity">
        <MyTeamActivityChart
          colorsByActivityType={colorsByActivityType}
          data={data?.activitiesData}
          fromDate={fromDate}
          loading={loading}
          namesByActivityType={namesByActivityType}
          numberOfActivitiesByType={data?.numberOfActivitiesByType}
          onActivityTypeChange={(type) => setSelectedActivityType(type)}
          range={range}
          selectedActivityType={selectedActivityType}
          toDate={toDate}
          totalWorkloadInHours={data?.totalWorkloadInHours}
        />

        <MyTeamActivityList
          activities={data?.activities}
          colorsByActivityType={colorsByActivityType}
          key={`${fromDate}-${toDate}-${selectedActivityType}`}
          loading={loading}
        />

        <style jsx>{`
          .activity {
            display: flex;
            flex-direction: column;
            gap: 30px;
          }
        `}</style>
      </div>
    </Box>
  );
};

export default MyTeamActivity;
